import en from "./dictionaries/en.json";
import {
  DEFAULT_LANGUAGE,
  KIOSK_DEFAULT_LANGUAGE,
  KIOSK_LANGUAGES,
  LANG_STORAGE_KEY,
  isKioskLanguage,
  type KioskLanguage,
} from "./languages";
import type { Dictionary } from "./types";

/**
 * G-85 — module-level language state, outside React on purpose.
 *
 * Two consumers cannot reach a context: code that runs before React mounts
 * (`preloadStoredLanguage` in `main.tsx`, so a Russian tablet's first paint is
 * already Russian) and plain functions that build user-facing text
 * (`platform/nfc.ts`, `shopFloorFormat.ts`, `ShopModeContext`). They read this
 * store through `t()`; React components subscribe to it through `useT()`.
 *
 * The active language is "en" whenever the kiosk is NOT mounted —
 * `LanguageProvider` activates on mount and deactivates on unmount, the same
 * shape as `lockTheme`/`unlockTheme`. That is what keeps the rest of the CRM
 * English even though some of its components (LogWasteModal, the task kit)
 * are shared with the kiosk and will go through `t()`.
 *
 * Nothing here touches `window` or `document` at import time: the root jest
 * project runs in a node environment.
 */

const dictionaries: Partial<Record<KioskLanguage, Dictionary>> = { en };
const pending = new Map<KioskLanguage, Promise<Dictionary>>();
const listeners = new Set<() => void>();

let active: KioskLanguage = DEFAULT_LANGUAGE;
/** Bumped on every change a subscriber could care about; the store snapshot. */
let version = 0;

/**
 * One explicit loader per language so Vite emits one chunk per dictionary.
 * A template-string `import()` would work too, but this way an unknown code
 * is a type error rather than a 404 at the counter.
 */
const LOADERS: Record<
  Exclude<KioskLanguage, "en">,
  () => Promise<{ default: Dictionary }>
> = {
  es: () => import("./dictionaries/es.json"),
  ca: () => import("./dictionaries/ca.json"),
  ru: () => import("./dictionaries/ru.json"),
  uk: () => import("./dictionaries/uk.json"),
};

function notify() {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getVersion(): number {
  return version;
}

export function activeLanguage(): KioskLanguage {
  return active;
}

export function englishDictionary(): Dictionary {
  return en;
}

/** Undefined while the chunk is still on its way — `translate` then falls back to English. */
export function activeDictionary(): Dictionary | undefined {
  return dictionaries[active];
}

export function isLoaded(language: KioskLanguage): boolean {
  return dictionaries[language] !== undefined;
}

export function loadLanguage(language: KioskLanguage): Promise<Dictionary> {
  const loaded = dictionaries[language];
  if (loaded) return Promise.resolve(loaded);
  const inFlight = pending.get(language);
  if (inFlight) return inFlight;
  if (language === "en") return Promise.resolve(en);
  const promise = LOADERS[language]()
    .then((module) => {
      dictionaries[language] = module.default;
      pending.delete(language);
      // The chunk arrived after the language was activated — repaint.
      if (active === language) notify();
      return module.default;
    })
    .catch((error: unknown) => {
      pending.delete(language);
      throw error;
    });
  pending.set(language, promise);
  return promise;
}

/**
 * Switch what `t()` reads. Safe to call before the dictionary is loaded: the
 * screen shows English until the chunk lands, then repaints — never blank.
 */
export function activateLanguage(language: KioskLanguage): void {
  if (active === language) return;
  active = language;
  notify();
}

/** Back to English — the kiosk unmounted, the CRM is on screen. */
export function deactivateLanguage(): void {
  activateLanguage(DEFAULT_LANGUAGE);
}

/**
 * What this TABLET shows before anyone signs in: the last language chosen on
 * it, else the kiosk's default (Spanish, owner ruling 2026-09-20). Never the
 * store's English — that is the CRM's language, not the counter's.
 */
export function storedTabletLanguage(): KioskLanguage {
  return rememberedTabletLanguage() ?? KIOSK_DEFAULT_LANGUAGE;
}

/**
 * The raw remembered value, with NO default applied — `null` means nobody has
 * ever chosen a language on this browser, which is also how the preload tells
 * a kiosk tablet from an ordinary CRM browser.
 */
export function rememberedTabletLanguage(): KioskLanguage | null {
  try {
    const raw = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (isKioskLanguage(raw)) return raw;
  } catch {
    // Blocked storage: treat it as never chosen.
  }
  return null;
}

export function rememberTabletLanguage(language: KioskLanguage): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, language);
  } catch {
    // Honoured this session, forgotten on restart — the printer registry's rule.
  }
}

/**
 * Called from `main.tsx`: start fetching the kiosk dictionary before React
 * mounts, so the lock screen's first paint does not flash English.
 * Fire-and-forget; a failure is retried by the provider and costs only the
 * flash it was trying to prevent.
 *
 * Two ways to qualify, because the first draft only had the second and so
 * missed the commonest path (G-85 S6 review): a browser that has EVER had a
 * language chosen on it is a kiosk tablet, and preloads whatever it
 * remembers no matter which route it opened on — which covers reaching the
 * kiosk by in-app navigation, where `main.tsx` has long since run. A browser
 * with nothing remembered only preloads when it opened ON the kiosk, so an
 * ordinary CRM session never fetches a dictionary it will not render.
 */
export function preloadKioskLanguage(openedOnKioskRoute: boolean): void {
  const remembered = rememberedTabletLanguage();
  if (!remembered && !openedOnKioskRoute) return;
  const target = remembered ?? KIOSK_DEFAULT_LANGUAGE;
  // English is bundled with the app; there is no chunk to fetch.
  if (target !== DEFAULT_LANGUAGE) void loadLanguage(target).catch(() => undefined);
  warmRemainingKioskLanguages(target);
}

/**
 * G-101 (owner correction, 2026-09-22) — warm EVERY kiosk dictionary, not just
 * the one this tablet remembers.
 *
 * The model this replaces was one tablet, one language. The owner's counter is
 * not that: *"the same tablet can be used by 5 people speaking different
 * languages just in 2 days"*. Under that model a per-tablet preload helps only
 * the first florist of the day — everyone after them PINs in and watches the
 * screen sit in English until their chunk lands.
 *
 * All four cost 74 KB gzipped, fetched once and then served from a year-long
 * immutable cache. Splitting is KEPT (the owner's call, and the right one: the
 * CRM-wide translation will multiply these, and a deploy that changes no words
 * must not re-send them), so this buys the switching speed of a bundle without
 * giving up the cache granularity of a chunk.
 *
 * DEFERRED, and that is the point — `requestIdleCallback` where it exists, a
 * timeout where it does not. The language actually in use is already in flight
 * above; these four must never compete with it, or with the first paint, on a
 * tablet whose shop wifi is the slowest thing in the building.
 */
function warmRemainingKioskLanguages(inFlight: KioskLanguage): void {
  const rest = KIOSK_LANGUAGES.map((l) => l.code).filter(
    (code) => code !== inFlight && code !== DEFAULT_LANGUAGE,
  );
  if (rest.length === 0) return;
  const warm = () => {
    for (const code of rest) {
      // Failure is silence on purpose: this is a convenience, and the language
      // a florist actually picks is fetched again (and reported) on demand.
      void loadLanguage(code).catch(() => undefined);
    }
  };
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (typeof idle === "function") idle(warm);
  else setTimeout(warm, 3_000);
}
